import { ProductFeatures } from '@trello/product-features';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { isProblem, getExpirationDate } from '@trello/paid-account';
import { Auth } from 'app/scripts/db/auth';
import { isDesktop as _isDesktop } from '@trello/browser';
const isDesktop = _isDesktop();
import moment from 'moment';
import { NoticeBanner } from 'app/src/components/NoticeBanner';
import React from 'react';
import { forTemplate } from '@trello/i18n';
import { navigate } from 'app/scripts/controller/navigate';

const format = forTemplate('board_limits');

class DowngradePeriodBanner extends React.Component {
  constructor(props) {
    super(props);
    this.dismissBanner = this.dismissBanner.bind(this);
    this.navigateToBillingPage = this.navigateToBillingPage.bind(this);

    const typeName = props.model?.typeName;
    const org =
      typeName === 'Board'
        ? props.model.getOrganization()
        : typeName === 'Organization'
        ? props.model
        : null;
    const paidAccount = org?.get('paidAccount') ?? null;
    const currentProduct = paidAccount?.paidProduct;

    const isStandardToFreeDowngrade = ProductFeatures.isStandardProduct(
      currentProduct,
    );

    const isBcToStandardDowngrade =
      paidAccount &&
      ProductFeatures.isStandardProduct(
        paidAccount?.scheduledChange?.ixSubscriptionProduct,
      ) &&
      org.isBusinessClass();

    const isRetiredBC = ProductFeatures.isRetiredBusinessClassProduct(
      currentProduct,
    );

    const bannerDismissed = false;
    let showDowngradeBanner = false;
    let showForcedDowngradeBanner = false;

    if (!paidAccount || (!org.isBusinessClass() && !org.isStandard())) {
      this.state = {
        org,
        bannerDismissed,
        showDowngradeBanner,
        showForcedDowngradeBanner,
        isBcToStandardDowngrade,
        isStandardToFreeDowngrade,
        isRetiredBC: isRetiredBC,
      };
    } else {
      const { datePendingDisabled } = paidAccount;
      const expirationDate = getExpirationDate(paidAccount);
      showDowngradeBanner =
        expirationDate &&
        moment().isBefore(expirationDate) &&
        moment().isSameOrAfter(moment(expirationDate).subtract(30, 'days'));

      showForcedDowngradeBanner =
        isProblem(paidAccount.standing) && paidAccount.needsCreditCardUpdate;
      this.state = {
        org,
        bannerDismissed,
        showDowngradeBanner,
        showForcedDowngradeBanner,
        expirationDate,
        datePendingDisabled,
        isBcToStandardDowngrade,
        isStandardToFreeDowngrade,
        isRetiredBC: isRetiredBC,
      };
    }
  }

  render() {
    if (
      isDesktop ||
      !(this.state.showDowngradeBanner || this.state.showForcedDowngradeBanner)
    ) {
      return null;
    }

    if (
      !Auth.me().isDismissed(
        `BoardLimitsDowngradePeriod-${this.state.org?.id}`,
      ) &&
      !this.state.showForcedDowngradeBanner &&
      this.state.showDowngradeBanner
    ) {
      const downgradeCopy = this.state.isBcToStandardDowngrade
        ? this.state.isRetiredBC
          ? 'downgrade-period-retired-BC-standard'
          : 'downgrade-period-BC-standard'
        : this.state.isStandardToFreeDowngrade
        ? 'downgrade-period-standard-free'
        : this.state.isRetiredBC
        ? 'downgrade-period-retired-bc'
        : 'downgrade-period';
      return (
        <div className="board-limits-downgrade-period-banner">
          <NoticeBanner
            // eslint-disable-next-line react/jsx-no-bind
            onDismiss={this.dismissBanner.bind(
              this,
              `BoardLimitsDowngradePeriod-${this.state.org?.id}`,
            )}
            actionButton={{
              text: this.state.isRetiredBC
                ? format('restore-retired-business-class')
                : this.state.isStandardToFreeDowngrade
                ? format('restore-standard')
                : format('restore-business-class'),
              onClick: this.navigateToBillingPage,
            }}
          >
            {format(downgradeCopy, {
              downgradeDate: moment(this.state.expirationDate).format('LL'),
              orgName: <>{this.state.org.get('displayName')}</>,
            })}
          </NoticeBanner>
        </div>
      );
    }

    if (
      !Auth.me().isDismissed(
        `BoardLimitsForcedDowngradePeriod-${this.state.org?.id}`,
      ) &&
      this.state.showForcedDowngradeBanner
    ) {
      return (
        <div className="board-limits-downgrade-period-banner">
          <NoticeBanner
            // eslint-disable-next-line react/jsx-no-bind
            onDismiss={this.dismissBanner.bind(
              this,
              `BoardLimitsForcedDowngradePeriod-${this.state.org?.id}`,
            )}
            actionButton={{
              text: format('restore-business-class'),
              onClick: this.navigateToBillingPage,
            }}
          >
            {this.state.datePendingDisabled
              ? format('forced-downgrade-message-expiration', {
                  orgName: <>{this.state.org.get('displayName')}</>,
                  pendingDisabledDate: moment(
                    this.state.datePendingDisabled,
                  ).format('LL'),
                })
              : format('forced-downgrade-message', {
                  orgName: <>{this.state.org.get('displayName')}</>,
                })}
          </NoticeBanner>
        </div>
      );
    }

    return null;
  }

  componentDidMount() {
    if (
      this.state.showDowngradeBanner ||
      this.state.showForcedDowngradeBanner
    ) {
      Analytics.sendViewedBannerEvent({
        bannerName: this.state.showForcedDowngradeBanner
          ? 'boardLimitsForcedDowngradeBanner'
          : 'boardLimitsDowngradeBanner',
        source: getScreenFromUrl(),
        containers: {
          organization: {
            id: this.state.org?.id,
          },
        },
      });
      this.props.setShown(true);
    }
    this.props.setShown(false);
  }

  dismissBanner(bannerId) {
    Auth.me().dismiss(bannerId);
    this.setState({
      bannerDismissed: true,
    });

    Analytics.sendDismissedComponentEvent({
      componentType: 'banner',
      componentName: this.state.showForcedDowngradeBanner
        ? 'boardLimitsForcedDowngradeBanner'
        : 'boardLimitsDowngradeBanner',
      source: getScreenFromUrl(),
      containers: {
        organization: {
          id: this.state.org?.id,
        },
      },
    });
  }

  navigateToBillingPage() {
    Analytics.sendClickedButtonEvent({
      buttonName: 'restoreBCButton',
      source: this.state.showForcedDowngradeBanner
        ? 'boardLimitsForcedDowngradeBanner'
        : 'boardLimitsDowngradeBanner',
      containers: {
        organization: {
          id: this.state.org?.id,
        },
      },
    });

    return navigate(this.state.org.getBillingUrl(), { trigger: true });
  }
}

export { DowngradePeriodBanner };
